import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, KeyRound, Mail } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { InvitationPreview } from "@syllarys/contracts";
import { apiClient, effectiveRole, useEms } from "../state/EmsContext";
import { useBrand } from "../state/BrandContext";

function passwordIssue(password: string, confirmation: string): string {
  if (password.length < 8)
    return "Le mot de passe doit contenir au moins 8 caractères.";
  if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
    return "Le mot de passe doit contenir des lettres et des chiffres.";
  if (password !== confirmation) return "Les deux saisies diffèrent.";
  return "";
}

/**
 * Récupération d'accès et activation d'invitation. Sans jeton dans l'URL, la
 * page demande l'e-mail ; avec un jeton, elle permet de définir le mot de passe.
 */
export function RecoveryPage({
  mode,
}: {
  mode: "recovery" | "invitation";
}): React.ReactNode {
  const { brand } = useBrand();
  const { refreshSession } = useEms();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (mode !== "invitation" || !token) return;
    let active = true;
    apiClient
      .getInvitation(token)
      .then((preview) => {
        if (active) setInvitation(preview);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "Invitation introuvable.",
          );
      });
    return () => {
      active = false;
    };
  }, [mode, token]);

  async function requestRecovery(): Promise<void> {
    setBusy(true);
    setError("");
    try {
      await apiClient.requestPasswordRecovery(email);
      setSent(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Envoi impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function definePassword(): Promise<void> {
    const issue = passwordIssue(password, confirmation);
    if (issue) {
      setError(issue);
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (mode === "invitation") {
        const session = await apiClient.acceptInvitation(token, password);
        await refreshSession();
        void navigate(
          effectiveRole(session) === "super_admin"
            ? "/app/admin/audit"
            : "/app/portfolio",
        );
        return;
      }
      await apiClient.resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Opération impossible.",
      );
    } finally {
      setBusy(false);
    }
  }

  const hasToken = Boolean(token);

  return (
    <div className="auth-simple-page">
      <Link to="/login">
        <ArrowLeft size={16} /> Retour à la connexion
      </Link>
      <section>
        <div className="login-logo dark">
          <img src={brand.logoDarkUrl} alt={brand.displayName} />
        </div>
        {done ? (
          <>
            <CheckCircle2 className="success-icon" />
            <h1>Mot de passe remplacé</h1>
            <p>
              Toutes vos sessions ont été fermées. Reconnectez-vous avec votre
              nouveau mot de passe.
            </p>
            <Link className="primary-button" to="/login">
              Se connecter
            </Link>
          </>
        ) : hasToken ? (
          <>
            <KeyRound className="auth-large-icon" />
            <p className="eyebrow">
              {mode === "recovery"
                ? "NOUVEAU MOT DE PASSE"
                : "ACTIVATION DU COMPTE"}
            </p>
            <h1>
              {mode === "recovery"
                ? "Choisissez un nouveau mot de passe"
                : invitation
                  ? `Bienvenue ${invitation.firstName}`
                  : "Activer votre accès"}
            </h1>
            <p>
              {mode === "invitation" && invitation
                ? `${invitation.brandName} vous a ouvert un accès pour ${invitation.email}. Choisissez votre mot de passe pour activer votre compte.`
                : "Au moins 8 caractères, avec des lettres et des chiffres."}
            </p>
            <label htmlFor="new-password">Mot de passe</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <label htmlFor="confirm-password">Confirmation</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
            {error ? <p className="form-error">{error}</p> : null}
            <button
              className="primary-button full"
              onClick={() => void definePassword()}
              disabled={busy || (mode === "invitation" && !invitation)}
            >
              {mode === "recovery"
                ? "Enregistrer le mot de passe"
                : "Activer mon compte"}
            </button>
          </>
        ) : sent ? (
          <>
            <CheckCircle2 className="success-icon" />
            <h1>Vérifiez votre messagerie</h1>
            <p>
              Si un compte correspond à cette adresse, un lien à usage unique
              valable une heure vient d’être envoyé.
            </p>
            <Link className="primary-button" to="/login">
              Revenir à la connexion
            </Link>
          </>
        ) : (
          <>
            <Mail className="auth-large-icon" />
            <p className="eyebrow">
              {mode === "recovery"
                ? "RÉCUPÉRATION D’ACCÈS"
                : "ACTIVATION DU COMPTE"}
            </p>
            <h1>
              {mode === "recovery"
                ? "Retrouver votre accès"
                : "Accepter l’invitation"}
            </h1>
            <p>
              {mode === "recovery"
                ? "Nous vous enverrons un lien temporaire pour définir un nouveau mot de passe."
                : "Utilisez le lien reçu par e-mail : il contient votre jeton d’activation. Si vous ne l’avez plus, demandez un nouvel envoi à votre Energy Manager."}
            </p>
            {mode === "recovery" ? (
              <>
                <label htmlFor="recovery-email">Adresse e-mail</label>
                <input
                  id="recovery-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {error ? <p className="form-error">{error}</p> : null}
                <button
                  className="primary-button full"
                  onClick={() => void requestRecovery()}
                  disabled={busy || !email.trim()}
                >
                  Envoyer le lien
                </button>
              </>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
